import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, CheckCircle2, FileText, Globe, TrendingUp, Users, 
  QrCode, ClipboardCheck, Package, Leaf, Target, Handshake,
  Award, Building2, Truck, BarChart3, Eye, Heart
} from 'lucide-react';
import SEO from '../components/SEO';

const About = () => {
  const stats = [
    { label: 'Products Exported', value: '50+', sublabel: 'Varieties' },
    { label: 'Countries Served', value: '25+', sublabel: 'Global Markets' },
    { label: 'Quality Pass Rate', value: '98.5%', sublabel: 'Consistency' },
    { label: 'On-Time Delivery', value: '99.2%', sublabel: 'Reliability' },
    { label: 'Traceability', value: '100%', sublabel: 'Coverage' },
  ];

  const values = [
    { icon: Eye, title: 'Transparency', desc: 'Every shipment includes complete farm-to-fork documentation accessible anytime' },
    { icon: Award, title: 'Quality First', desc: '6-gate inspection process with zero compromise — we reject what doesn\'t meet standards' },
    { icon: BarChart3, title: 'Technology Enabled', desc: 'Digital traceability, real-time tracking, instant verification at your fingertips' },
    { icon: Handshake, title: 'Partner Success', desc: 'Your growth is our measure of success — dedicated support for every buyer' },
    { icon: Leaf, title: 'Ethical Sourcing', desc: 'Direct relationships with certified farms committed to sustainable practices' },
  ];

  const products = [
    { icon: '🌶️', name: 'Spices', items: 'Turmeric, Cumin, Coriander, Chili, Black Pepper, Cardamom & 20+ varieties' },
    { icon: '🥜', name: 'Dry Fruits', items: 'Cashews, Almonds, Pistachios, Walnuts, Raisins, Figs, Dates' },
    { icon: '🫒', name: 'Edible Oils', items: 'Groundnut, Sesame, Coconut, Mustard, Sunflower' },
    { icon: '🌾', name: 'Grains & Pulses', items: 'Basmati Rice, Non-Basmati Rice, Lentils, Chickpeas' },
    { icon: '🍃', name: 'Wellness', items: 'Organic products, Superfoods, Health-focused commodities' },
    { icon: '🧅', name: 'Dehydrated Aromatics', items: 'Onion, Garlic, Ginger & specialty dehydrated vegetables' },
  ];

  const certifications = [
    'FSSAI Certified',
    'APEDA Registered',
    'Spices Board India',
    'ISO 22000',
    'HACCP Compliant',
    'Organic Certified'
  ];

  return (
    <div className="w-full">
      <SEO 
        title="About SPANXPORT - Powering Intelligent Global Trade"
        description="Learn about SPANXPORT's mission to connect India's finest agricultural products to the world with technology-driven transparency, verified quality, and complete traceability. EximStack powered."
        keywords="about SPANXPORT, Indian exports company, agricultural trade, quality assurance, traceability, global trade"
        url="/about"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-gradient-to-br from-[#263776] via-[#1e2d5f] to-[#263776] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#F47D20] rounded-full filter blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#F47D20] rounded-full filter blur-3xl translate-x-1/2 translate-y-1/2"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-8 inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_spanx-export/artifacts/xlq8jb39_SPANXPORT%20Logo%20Master.png" 
                alt="SPANXPORT" 
                className="h-20 mb-2 mx-auto brightness-0 invert"
              />
              <p className="text-[#F47D20] font-bold tracking-[0.3em] text-sm">
                POWERING INTELLIGENT GLOBAL TRADE
              </p>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Who We Are</h1>
            <p className="text-xl md:text-2xl text-white/90 leading-relaxed max-w-3xl mx-auto">
              Connecting India's finest agricultural products to the world — with technology-driven transparency, verified quality, and complete traceability.
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-slate-700 leading-relaxed mb-6">
                <span className="font-bold text-[#263776]">SPANXPORT</span> is a technology-driven agricultural commodities trading company bridging farmers and global markets. We specialize in exporting premium Indian spices, dry fruits, edible oils, grains, and wellness products to buyers across the Middle East, Europe, Americas, and Asia-Pacific.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-6">
                Founded with a vision to transform how agricultural commodities move across borders, we asked a simple question: <em className="text-[#263776] font-medium">Why is international commodity trading still plagued by opacity, documentation delays, and quality inconsistencies?</em>
              </p>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our answer was to build a company where <strong className="text-[#F47D20]">technology serves trust</strong>. Today, we combine decades of trading expertise with cutting-edge supply chain management to deliver not just products — but confidence.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-8 border-l-4 border-l-[#263776] hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-8 h-8 text-[#263776]" />
                  <h3 className="text-2xl font-bold text-[#263776]">Our Mission</h3>
                </div>
                <p className="text-slate-700 text-lg leading-relaxed">
                  To be the most transparent and reliable bridge between Indian agricultural excellence and global demand — powered by technology, driven by integrity.
                </p>
              </Card>

              <Card className="p-8 border-l-4 border-l-[#F47D20] hover:shadow-xl transition-shadow">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="w-8 h-8 text-[#F47D20]" />
                  <h3 className="text-2xl font-bold text-[#263776]">Our Vision</h3>
                </div>
                <p className="text-slate-700 text-lg leading-relaxed">
                  A world where every food product can be traced back to its source, where quality is verified not claimed, and where international trade empowers farmers and buyers alike.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart - EximStack */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <Badge className="bg-[#F47D20]/10 text-[#F47D20] mb-4 text-sm px-4 py-1">WHAT SETS US APART</Badge>
              <h2 className="text-4xl font-bold text-[#263776] mb-4">
                Introducing <span className="text-[#F47D20]">EximStack</span>
              </h2>
              <p className="text-xl text-slate-600 max-w-3xl mx-auto">
                Our proprietary supply chain intelligence platform that brings unprecedented transparency to every transaction.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-8 hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-[#263776]/20">
                <div className="w-16 h-16 bg-[#263776]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#263776] transition-colors">
                  <Package className="w-8 h-8 text-[#263776] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#263776] mb-3">Farm-to-Fork Traceability</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every shipment comes with a digital dossier tracking the complete journey — from the farm where it was grown to your warehouse door. Know exactly where your product comes from.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-[#F47D20]/20">
                <div className="w-16 h-16 bg-[#F47D20]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#F47D20] transition-colors">
                  <ClipboardCheck className="w-8 h-8 text-[#F47D20] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#263776] mb-3">6-Gate Quality Assurance</h3>
                <p className="text-slate-600 leading-relaxed">
                  Rigorous inspection at six critical stages: pre-harvest, post-harvest, processing, storage, pre-shipment, and arrival. Nothing ships without verification.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-[#263776]/20">
                <div className="w-16 h-16 bg-[#263776]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#263776] transition-colors">
                  <FileText className="w-8 h-8 text-[#263776] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#263776] mb-3">Real-Time Documentation</h3>
                <p className="text-slate-600 leading-relaxed">
                  All export documents digitized, verified, and accessible via our secure portal. No more chasing paperwork across time zones — everything you need, when you need it.
                </p>
              </Card>

              <Card className="p-8 hover:shadow-xl transition-all duration-300 group border-2 border-transparent hover:border-[#F47D20]/20">
                <div className="w-16 h-16 bg-[#F47D20]/10 rounded-2xl flex items-center justify-center mb-6 group-hover:bg-[#F47D20] transition-colors">
                  <QrCode className="w-8 h-8 text-[#F47D20] group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-bold text-[#263776] mb-3">QR-Enabled Verification</h3>
                <p className="text-slate-600 leading-relaxed">
                  Every batch includes a unique QR code. Scan to instantly verify product origin, certifications, quality reports, and complete chain of custody.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#263776] mb-4">Our Values</h2>
              <p className="text-xl text-slate-600">The principles that guide everything we do</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {values.map((value, idx) => (
                <Card key={idx} className="p-6 hover:shadow-lg transition-shadow border-t-4 border-t-[#F47D20]">
                  <value.icon className="w-10 h-10 text-[#263776] mb-4" />
                  <h3 className="text-lg font-bold text-[#263776] mb-2">{value.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{value.desc}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Our Products */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#263776] mb-4">Our Products</h2>
              <p className="text-xl text-slate-600">We source and export India's finest agricultural products</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, idx) => (
                <Card key={idx} className="p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="text-4xl mb-4">{product.icon}</div>
                  <h3 className="text-xl font-bold text-[#263776] mb-2">{product.name}</h3>
                  <p className="text-slate-600 text-sm">{product.items}</p>
                </Card>
              ))}
            </div>

            <div className="text-center mt-10">
              <Button asChild size="lg" className="bg-[#263776] hover:bg-[#1e2a5e]">
                <Link to="/products">Explore All Products</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Buyers Choose Us */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#263776] mb-4">Why Buyers Choose Us</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { icon: Shield, title: 'Verified Sourcing', desc: 'Direct relationships with certified farms and processors' },
                { icon: CheckCircle2, title: 'Quality Guaranteed', desc: 'Multi-stage inspection with photo documentation and lab reports' },
                { icon: FileText, title: 'Compliance Ready', desc: 'Documentation prepared for FDA, EU, GCC, and other regulations' },
                { icon: Globe, title: 'Digital Transparency', desc: 'Access your shipment\'s complete history via our EximStack portal' },
                { icon: Users, title: 'Responsive Support', desc: 'Dedicated account management for every buyer' },
                { icon: Truck, title: 'Reliable Delivery', desc: '99.2% on-time delivery rate with real-time tracking' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
                  <div className="w-12 h-12 bg-[#263776]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-[#263776]" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#263776] mb-1">{item.title}</h3>
                    <p className="text-slate-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Numbers That Matter */}
      <section className="py-20 bg-[#263776] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold mb-4">Numbers That Matter</h2>
              <p className="text-xl text-white/80">Our track record speaks for itself</p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              {stats.map((stat, idx) => (
                <div key={idx} className="text-center p-6 bg-white/10 rounded-xl backdrop-blur-sm">
                  <div className="text-4xl md:text-5xl font-bold text-[#F47D20] mb-2">{stat.value}</div>
                  <div className="text-white font-medium">{stat.label}</div>
                  <div className="text-white/60 text-sm">{stat.sublabel}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Who We Work With */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold mb-12 text-center text-[#263776]">Who We Work With</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card className="p-8 border-2 border-[#263776]/10 hover:border-[#263776]/30 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#263776] rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#263776]">For Buyers</h3>
                </div>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20]" />
                    Importers and distributors
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20]" />
                    Retail procurement teams
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20]" />
                    Food manufacturers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20]" />
                    Private label owners
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#F47D20]" />
                    Institutional buyers
                  </li>
                </ul>
                <Button asChild className="mt-6 w-full bg-[#263776] hover:bg-[#1e2a5e]">
                  <Link to="/buyer-application">Become a Verified Buyer</Link>
                </Button>
              </Card>

              <Card className="p-8 border-2 border-[#F47D20]/10 hover:border-[#F47D20]/30 transition-colors">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-[#F47D20] rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-[#263776]">For Sellers</h3>
                </div>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#263776]" />
                    Food commodity producers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#263776]" />
                    Agricultural cooperatives
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#263776]" />
                    Processing facilities
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#263776]" />
                    Export-ready suppliers
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-[#263776]" />
                    Wholesale distributors
                  </li>
                </ul>
                <Button asChild className="mt-6 w-full bg-[#F47D20] hover:bg-[#d66b1a]">
                  <Link to="/seller-application">Become a Verified Seller</Link>
                </Button>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-[#263776] mb-4">Certifications & Compliance</h2>
              <p className="text-slate-600">We maintain the highest standards of quality and compliance</p>
            </div>

            <div className="flex flex-wrap justify-center gap-4">
              {certifications.map((cert, idx) => (
                <Badge key={idx} variant="outline" className="px-6 py-3 text-base border-[#263776]/30 text-[#263776] hover:bg-[#263776] hover:text-white transition-colors cursor-default">
                  <Award className="w-4 h-4 mr-2" />
                  {cert}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-[#263776] mb-4">Leadership & Team</h2>
              <p className="text-xl text-slate-600">The people driving SPANXPORT's mission forward</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#263776]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Pankaj Monga</h3>
                <p className="text-[#F47D20] font-semibold mb-4">CEO & Managing Director</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Pankaj leads SPANXPORT's strategy, partner development, and execution discipline. He focuses on building repeatable trade lanes with a strong emphasis on verification, risk control, and buyer-ready delivery.
                </p>
                <blockquote className="mt-4 pt-4 border-t border-slate-200 text-sm italic text-slate-500">
                  "We built SPANXPORT to prove that commodity trading can be transparent, technology-enabled, and relationship-driven."
                </blockquote>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#F47D20]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Harvinder Kaur Wasan</h3>
                <p className="text-[#F47D20] font-semibold mb-4">Director, Quality Systems</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Harvinder oversees quality systems design, QA gates, and operational excellence to ensure consistent specification adherence and audit-ready evidence capture.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#263776]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Davinder Singh Wasan</h3>
                <p className="text-[#F47D20] font-semibold mb-4">Director, International Sales & Marketing</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Davinder leads international sales, distributor partnerships, and commercial enablement across priority markets, supporting repeat-lane growth and partner onboarding.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#F47D20]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Joydeep Roy</h3>
                <p className="text-[#F47D20] font-semibold mb-4">COO & Head of Supply Chain and Finance</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Joydeep supports international business development, partner strategy, and execution frameworks across trade corridors, focusing on scalable operations and governance.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#263776]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Rubel Ahamed</h3>
                <p className="text-[#F47D20] font-semibold mb-4">Country Director (Bangladesh)</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Rubel leads Bangladesh operations and regional partner coordination, supporting cross-border execution and local stakeholder management.
                </p>
              </Card>

              <Card className="p-6 hover:shadow-xl transition-shadow border-t-4 border-t-[#F47D20]">
                <h3 className="text-xl font-bold text-[#263776] mb-1">Shailja Monga</h3>
                <p className="text-[#F47D20] font-semibold mb-4">Director, HR & Administration</p>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Shailja leads people, culture, and administrative operations—driving hiring, HR policies, employee engagement, and office governance to build a high-performance work environment.
                </p>
              </Card>
            </div>

            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-2 px-8 py-4 bg-slate-50 rounded-full">
                <Heart className="w-5 h-5 text-[#F47D20]" />
                <p className="text-lg text-[#263776] font-semibold">
                  Integrity | Simplicity | Accountability | Quality | Partnership
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Promise */}
      <section className="py-16 bg-gradient-to-r from-[#263776] to-[#1e2d5f]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Our Promise</h2>
            <blockquote className="text-2xl text-white/90 italic leading-relaxed">
              "We don't just export commodities — we export trust. Every batch we ship carries complete traceability, verified quality certificates, and the assurance of ethical sourcing practices."
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-[#263776] mb-6">Ready to Trade with Confidence?</h2>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            A 15-30 minute discovery is enough to begin. Share your commodity requirement and we'll respond with a verified supply plan.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button asChild size="lg" className="bg-[#F47D20] text-white hover:bg-[#d66b1a]">
              <Link to="/contact">Contact Us</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
              <Link to="/products">View Products</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-[#263776] text-[#263776] hover:bg-[#263776] hover:text-white">
              <Link to="/verified-directory">Join Our Network</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
